import EditIcon from "@mui/icons-material/Edit"
import { Box, CircularProgress, DialogContentText, MenuItem } from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import { useFormik } from "formik"
import { useAppDispatch } from "hooks"
import { useState } from "react"
import organizationService from "services/organizationService"
import { updateOrganization } from "slices/organizationSlice"
import type { IOrganization, IUpdateOrganization } from "types/organizationTypes"
import * as Yup from "yup"

type Props = {
  organization: IOrganization
}

export function UpdateOrganizationDialog({ organization }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [open, setOpen] = useState(false)

  const dispatch = useAppDispatch()

  const initialValues: IUpdateOrganization = {
    name: organization.name,
    ownerId: organization.ownerId,
  }

  const updateOrganizationValidationScheme = Yup.object({
    name: Yup.string()
      .required("Please enter organization name")
      .min(4, "Minimum name length is 4 characters")
      .max(64, "Maximum name length is 64 characters"),
  })

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: updateOrganizationValidationScheme,
    onSubmit: (values) => {
      setIsLoading(true)
      const organizationUpdate: IUpdateOrganization = {
        name: values.name,
        ownerId: values.ownerId,
      }

      organizationService
        .updateOrganization(organization.id, organizationUpdate)
        .then((resp) => {
          dispatch(updateOrganization(resp))
          setIsLoading(false)
          handleClose()
        })
        .catch((err) => {
          console.log(err)
          setIsLoading(false)
        })
    },
  })

  const handleClickOpen = () => {
    formik.resetForm()
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <div>
      <MenuItem onClick={handleClickOpen}>
        <EditIcon sx={{ marginRight: 1 }} />
        Update
      </MenuItem>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>Update Organization</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Please enter new name for the organization and owner user.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Name"
              type="name"
              fullWidth
              variant="standard"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
            <TextField
              autoFocus
              margin="dense"
              id="owner"
              label="Owner"
              type="owner"
              fullWidth
              variant="standard"
              value={formik.values.ownerId}
              onChange={formik.handleChange}
              error={formik.touched.ownerId && Boolean(formik.errors.ownerId)}
              helperText={formik.touched.ownerId && formik.errors.ownerId}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button type="submit" disabled={isLoading}>
                Update
              </Button>
              {isLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>{" "}
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}
